import { useMemo } from "react";

import { PERMISSIONS } from "shared/Actions/constants";

import { RAW_CONFIG, RECOMMENDED_PERMISSIONS } from "./constants";
import { AccessSection, FormValues } from "./types";

/**
 * Hook that returns the role form configuration.
 * Memoized to prevent unnecessary re-renders.
 * @returns {AccessSection[]} The configuration sections with permissions
 */
export const useRoleFormConfig = (): AccessSection[] => {
  return useMemo(
    () =>
      RAW_CONFIG.map((section) => ({
        ...section,
        permissions: section.permissions.map((permission) => ({
          action: permission,
          id: PERMISSIONS[permission].name,
          description: PERMISSIONS[permission].description || "",
          tooltipInfo: PERMISSIONS[permission].tooltipInfo,
          tooltipInfoVariant: PERMISSIONS[permission].tooltipInfoVariant,
        })),
      })),
    []
  );
};

/**
 * Hook that returns the default form values based on the current config.
 * Memoized to prevent unnecessary re-renders.
 * @returns {FormValues} The default form values
 */
export const useDefaultFormValues = (): FormValues => {
  const config = useRoleFormConfig();

  return useMemo(
    () => ({
      name: "",
      description: "",
      permissions: config.reduce((acc, section) => {
        return {
          ...acc,
          [section.id]: section.permissions.reduce(
            (acc, permission) => ({
              ...acc,
              [permission.action]: RECOMMENDED_PERMISSIONS.has(permission.action),
            }),
            {}
          ),
        };
      }, {}),
    }),
    [config]
  );
};
